import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, NavigationEnd, Router, RouterLink} from '@angular/router';
import {filter, map, Observable, startWith} from 'rxjs';
import {getLastChild, getRouteLabel, hasOwnRouteLabel} from '@shared/route-utils';
import {TranslateService} from '@brunata-angular/util';

type Breadcrumb = {
    label: string;
    url: string;
};

@Component({
    selector: 'app-breadcrumb',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, RouterLink],
    templateUrl: './breadcrumb.component.html',
    styleUrl: './breadcrumb.component.scss',
})
export class BreadcrumbComponent {
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly translateService = inject(TranslateService);

    readonly breadcrumbs$: Observable<Breadcrumb[]> = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(null),
        map(() => this.createBreadcrumbs(this.activatedRoute.root)),
    );

    private createBreadcrumbs(
        route: ActivatedRoute | null,
        url: string = '',
        breadcrumbs: Breadcrumb[] = [],
    ): Breadcrumb[] {
        if (route?.firstChild == null) {
            return breadcrumbs;
        }

        const routeURL: string = route.firstChild.snapshot.url
            .map(segment => segment.path)
            .join('/');
        if (routeURL !== '') {
            url += `/${routeURL}`;
        }

        if (route.parent === null) {
            // Skip generating breadcrumb for root element (admin or resident)
            return this.createBreadcrumbs(route.firstChild, url);
        }

        // An empty-path child adds no URL segment, so crumbing one that merely inherits its
        // parent's data would repeat the parent's crumb.
        if (routeURL === '' && !hasOwnRouteLabel(route.firstChild.snapshot)) {
            return this.createBreadcrumbs(route.firstChild, url, breadcrumbs);
        }

        const breadcrumb: Breadcrumb = {
            label: getRouteLabel(route.firstChild.snapshot, this.translateService) ?? routeURL,
            url: url,
        };
        return this.createBreadcrumbs(route.firstChild, url, [...breadcrumbs, breadcrumb]);
    }

    navigateToParent(): void {
        const lastChild = getLastChild(this.activatedRoute);
        let route = lastChild.parent;
        const indexChild = route?.routeConfig?.children?.find(child => child.path === '');
        const redirectsToChild: unknown = route?.routeConfig?.data?.['redirectsToChild'];
        // Step up an extra level when the parent resolves to the page we are already on: its bare
        // URL lands on a child, or the current page is that index route.
        const onIndexRoute = lastChild.snapshot.url.length === 0;
        if (indexChild?.redirectTo != null || redirectsToChild === true || onIndexRoute) {
            route = route?.parent ?? null;
        }
        void this.router.navigate(['.'], {relativeTo: route});
    }
}
